import {
  base_colorContentBorder,
  text_colorTextPrimary,
  text_colorTextSecondary,
} from 'ext-ui/style/colors'
import { spacing } from 'ext-ui/style/spacing'
import {
  font_family_weight_medium,
  font_family_weight_regular,
  font_family_weight_semibold,
  font_md,
  font_size_md,
  font_size_sm,
} from 'ext-ui/style/typography'
import { css } from 'styled-components'

/**
 * Matches `CardOverviewRecentTransactions.style` so the two tables on the
 * Overview tab read as one surface: trimmed vertical padding and no bottom
 * padding, because the grid's last row border is the panel's real edge.
 */
export const panel = css`
  display: flex;
  flex-direction: column;
  padding: ${spacing(6)} ${spacing(8)} 0;
`

export const header = css`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${spacing(4)};
  margin-bottom: ${spacing(3)};
`

/**
 * The uppercase section kicker shared with the Statements/Activity tabs and the
 * recent-transactions panel. Uppercased in CSS, never in the translation source,
 * so the accessible name stays the plain phrase.
 */
export const title = css`
  font-size: ${font_size_sm};
  font-family: ${font_family_weight_semibold};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${text_colorTextSecondary};
  margin: 0;
`

/**
 * The right-hand end of the heading row: the count and the New budget CTA sit
 * together there, per the design, so they need their own row box rather than
 * each being a bare flex child of the space-between header.
 */
export const headerActions = css`
  display: flex;
  align-items: center;
  gap: ${spacing(4)};
`

/** The "N budgets" count that sits opposite the title on the Budgets tab. */
export const count = css`
  font-size: ${font_size_sm};
  font-family: ${font_family_weight_regular};
  color: ${text_colorTextSecondary};
  white-space: nowrap;
`

/**
 * A column header, rendered by our own `renderHeader` rather than styled through
 * `.MuiDataGrid-columnHeaderTitle`.
 *
 * MUI wraps that title in its own emotion-styled element, so restyling it from
 * outside is a cascade race we lose intermittently. Supplying the element is the
 * pattern the rest of this codebase already uses (`BudgetDataGrid`, the
 * transactions column factory) and it is the only one that reliably wins.
 *
 * Uppercased in CSS, never in the translation source, so the accessible name —
 * which MUI takes from `headerName` — stays the plain phrase.
 */
export const columnHeader = css`
  font-size: ${font_size_sm};
  font-family: ${font_family_weight_medium};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${text_colorTextSecondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

/** The terminal column's header, right-aligned over its values. */
export const columnHeaderRight = css`
  ${columnHeader};
  width: 100%;
  text-align: right;
`

/** Keeps the grid's own top border from doubling the card's inner spacing. */
export const grid = css`
  border-top: 1px solid ${base_colorContentBorder};
`

/** Budget-name cell: the card-type glyph beside the name. */
export const nameCell = css`
  display: flex;
  align-items: center;
  gap: ${spacing(2)};
  min-width: 0;
`

export const nameGlyph = css`
  height: 20px;
  flex: 0 0 auto;
`

/**
 * The budget name. Medium, NOT bold: it is the row's primary cell, and the
 * design distinguishes it from its neighbours by weight alone at the same size.
 */
/**
 * The budget-name anchor. Inherits the cell's type; `color: inherit` and no
 * underline keep it reading as a table row rather than a link in prose, while
 * still being a real `<a href>` for ⌘-click, copy-link, hover preview and
 * screen-reader link enumeration.
 */
export const nameLink = css`
  display: flex;
  align-items: center;
  gap: ${spacing(2)};
  min-width: 0;
  color: inherit;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

/** A non-viewable budget's name — no anchor, since there is nowhere to go. */
export const nameCellStatic = css`
  display: flex;
  align-items: center;
  gap: ${spacing(2)};
  min-width: 0;
  color: ${text_colorTextSecondary};
`

export const nameText = css`
  font-size: ${font_size_md};
  font-family: ${font_family_weight_medium};
  color: ${text_colorTextPrimary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

/**
 * A plain text cell.
 *
 * Every column renders through an element of ours rather than letting MUI emit a
 * bare text node for the `valueGetter`-only ones. That was the difference behind
 * the Type and Cards columns sitting a notch right of their headers while Budget
 * and Owner — which already had a `renderCell` — lined up: MUI lays a raw text
 * node out differently from a flex child inside the same cell box. Uniform
 * structure removes the divergence by construction rather than by chasing a
 * padding override.
 */
export const textCell = css`
  font-size: ${font_size_md};
  font-family: ${font_family_weight_regular};
  color: ${text_colorTextPrimary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

/**
 * The terminal "spent / limit" pair. Tabular figures so the slash lines up down
 * the column, which is the whole reason the design pairs them in one cell.
 */
export const amountCell = css`
  width: 100%;
  text-align: right;
  font-size: ${font_size_md};
  font-variant-numeric: tabular-nums;
  color: ${text_colorTextPrimary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

/** Right-aligns the header of the terminal column over its values. */
export const amountHeader = css`
  width: 100%;
  text-align: right;
`

/**
 * Shared frame for the loading / empty / error bodies so the panel keeps a
 * stable height as it moves between them and the sections around it don't jump.
 */
export const statusBody = css`
  ${font_md};
  font-family: ${font_family_weight_regular};
  color: ${text_colorTextSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: ${spacing(24)};
  margin: 0;
  /* The panel drops its bottom padding for the grid's flush last-row border;
     the status bodies have no such border and need it back. */
  padding-bottom: ${spacing(6)};
`
